import React, { useState, useEffect, useCallback } from "react";
import {
  LiquityStoreState,
  Decimal,
  Trove,
  UserTrove,
} from "@liquity/lib-base";

import { useLiquitySelector } from "@liquity/lib-react";
import { useStableTroveChange } from "../../../hooks/useStableTroveChange";
import { InputField } from "../../PSY/InputField";
import { TrovePosition } from "./TrovePosition";
import { ActionDescription } from "../../ActionDescription";
import {
  selectForTroveChangeValidation,
  validateTroveChange
} from "../validation/validateTroveChange";
import { LoadingOverlay } from "../../LoadingOverlay";
import { CollateralLexicons, SLSD } from "src/lexicon";
import { useMyTransactionState } from "src/components/Transaction"
import { usePSYModal } from "../../../hooks/usePSYModal";
import { Closing } from "../Closing";
import { TroveAction } from "../TroveAction";
import { Button } from "../../PSY/Button"
import { ApproveAction } from "../ApproveAction";
import { Navigate } from "react-router-dom";
import { useAppState } from "../../../hooks/AppStateContext";

export type ActionProps = {
  stableTroveChange: ReturnType<typeof useStableTroveChange>
  transactionId: string
  maxBorrowingRate: Decimal
  stETHAllowance: Decimal
  approvalTransactionId: string
}

type Props = {
  trove: Trove
  updatedTrove: Trove
  maxCollateral: Decimal
  borrowingRate: Decimal
  collateral: Decimal
  setCollateral: React.Dispatch<React.SetStateAction<Decimal>>
  netDebt: Decimal
  setNetDebt: React.Dispatch<React.SetStateAction<Decimal>>
  transactionId: "trove-adjustment" | "trove-creation"
  actionLabel: string
}

const APPROVAL_TRANSACTION_ID = "approve-token";

export const TroveForm :React.FC<Props> = ({
  trove,
  updatedTrove,
  maxCollateral,
  borrowingRate,
  collateral,
  setCollateral,
  netDebt,
  setNetDebt,
  transactionId,
  actionLabel,
}) => {
  const { currentAssetAddress, currentCollateralKey } = useAppState();
  console.log("🚀 ~ currentCollateralKey:", currentCollateralKey)

  const selector = useCallback((state: LiquityStoreState) => {
    if (!currentCollateralKey) {
      return {
        trove: UserTrove.createEmpty(),
        price: Decimal.ZERO,
        balance: Decimal.ZERO,
        allowance: Decimal.ZERO,
        validationContext: {
          price: Decimal.ZERO,
          total: new Trove(Decimal.ZERO, Decimal.ZERO),
          balance: Decimal.ZERO,
          slsdBalance: Decimal.ZERO,
          numberOfTroves: 0,
        }
      }
    }
    const { collateralStates, collateralDerivedStates } = state;
    const { price, allowance, balance } = collateralStates[currentCollateralKey];
    const { trove } = collateralDerivedStates[currentCollateralKey];

    return {
      trove,
      price,
      balance,
      allowance,
      validationContext: selectForTroveChangeValidation(state, currentCollateralKey)
    };
  }, [currentCollateralKey]);

  const selectedState = useLiquitySelector(selector);
  const maxBorrowingRate = borrowingRate.add(0.005);

  const { price, balance, allowance, validationContext } = selectedState
  const lexicon = CollateralLexicons.find(lexicon => lexicon.key === currentCollateralKey)
  console.log("🚀 TroveForm ~ balance:", balance.toString());

  const [troveChange, description] = validateTroveChange(
    trove,
    updatedTrove,
    borrowingRate,
    validationContext,
    lexicon
  );
  const stableTroveChange = useStableTroveChange(troveChange);
  const isIncreaseCollateral = !!stableTroveChange?.params.depositCollateral
  const isSufficientAllowance = stableTroveChange?.params.depositCollateral?.lte(allowance) ?? false

  const transactionState = useMyTransactionState(transactionId);
  const isTransactionPending =
    transactionState.type === "waitingForApproval" ||
    transactionState.type === "waitingForConfirmation";

  const approveTransactionState = useMyTransactionState(APPROVAL_TRANSACTION_ID);
  const isApproveTransactionPending =
    approveTransactionState.type === "waitingForApproval" ||
    approveTransactionState.type === "waitingForConfirmation";

  const { openModal, closeModal, Modal } = usePSYModal(
    <>
      {!!currentCollateralKey && (
        <Closing
          collateral={Decimal.ZERO}
          debt={Decimal.ZERO}
          collateralKey={currentCollateralKey}
        />
      )}
    </>
  )

  const [isTransactionConfirmed, setIsTransactionConfirmed] = useState(false)
  useEffect(() => {
    setTimeout(() => {
      if (transactionState.type === "confirmed") {
        setIsTransactionConfirmed(true);
      }
    }, 500);
  }, [transactionState.type])

  if (isTransactionConfirmed) {
    return <Navigate to="/" />
  }

  return (
    <div className="bg-component p-5 rounded-xl border-component h-fit flex flex-col">
      <TrovePosition
        collateral={trove.collateral.prettify(2)}
        collateralUSDValue={trove.collateral.mul(price).prettify(2)}
        debt={trove.debt.prettify()}
        collateralRatio={trove.collateralRatio(price)}
        lexicon={lexicon}
      />

      <div>
        <InputField
          label="Collateral"
          amount={collateral.toString(4)}
          maxAmount={maxCollateral.toString()}
          usdValue={collateral.mul(price).prettify(2)}
          balance={balance.prettify(2)}
          unit={lexicon?.term}
          icon={lexicon?.iconUrl}
          setEditedAmount={(amount: string) => setCollateral(Decimal.from(amount))}
        />
        <InputField
          label="Net debt"
          amount={netDebt.toString(4)}
          unit={SLSD.term}
          icon={SLSD.iconUrl}
          setEditedAmount={(amount: string) => setNetDebt(Decimal.from(amount))}
        />

        {description ?? (
          <ActionDescription>
            {transactionId === "trove-creation" ?  "Start by entering the amount of collateral you'd like to deposit." : "Adjust your Trove by modifying its collateral, debt, or both."}
          </ActionDescription>
        )}

        {/* NOTE: In the case of ETH collateral, gas fees would be too expensive
        {!stETHAllowance.isZero && (
          <ExpensiveTroveChangeWarning
            troveChange={stableTroveChange}
            maxBorrowingRate={maxBorrowingRate}
            borrowingFeeDecayToleranceMinutes={60}
            gasEstimationState={gasEstimationState}
            setGasEstimationState={setGasEstimationState}
          />
        )} */}
        {!trove.isEmpty && (
          <>
            <Modal/>
            <div className="flex flex-row justify-end mb-2">
              <button onClick={openModal} className="text-style-sub">Close position</button>
            </div>
          </>
        )}
        <div className="flex flex-end">
          <>
            {!!currentAssetAddress && (
              <>
                <ApproveAction
                  transactionId={APPROVAL_TRANSACTION_ID}
                  disabled={!isIncreaseCollateral || isSufficientAllowance}
                  assetAddress={currentAssetAddress}
                >
                  {isSufficientAllowance ? "Approved" : "Approve"}
                </ApproveAction>
                {stableTroveChange ? (
                  <TroveAction
                    transactionId={transactionId}
                    change={stableTroveChange}
                    maxBorrowingRate={maxBorrowingRate}
                    borrowingFeeDecayToleranceMinutes={60}
                    disabled={isIncreaseCollateral && !isSufficientAllowance}
                    collateralAddress={currentAssetAddress}
                  >
                    {actionLabel}
                  </TroveAction>
                ) : (
                  <Button disabled={true} className="flex flex-1">{actionLabel}</Button>
                )}
              </>
            )}
          </>
        </div>
      </div>
      {(isTransactionPending  || isApproveTransactionPending) && <LoadingOverlay />}
    </div>
  );
}